import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { owmTileUrl } from "../lib/api";
import { WindLayer } from "./WindLayer";
import { ScalarLayer } from "./ScalarLayer";
import { FireLayer } from "./FireLayer";
import { SCALAR_LAYERS } from "../lib/colorScales";

const BASE = {
  dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
};
const LABELS = {
  dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
  light: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
};

const pin = L.divIcon({ className: "", html: '<div class="map-pin"><span></span></div>', iconSize: [24, 24], iconAnchor: [12, 12] });
const pickPin = L.divIcon({ className: "", html: '<div class="map-pin map-pin-pick"><span></span></div>', iconSize: [24, 24], iconAnchor: [12, 12] });

const DETAIL_ZOOM = 12;

const radarUrl = (host, frame) => `${host}${frame.path}/256/{z}/{x}/{y}/2/1_1.png`;

const Recenter = ({ lat, lon }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lon], map.getZoom(), { duration: 1.2 });
  }, [lat, lon, map]);
  return null;
};

const Events = ({ onPick, onBounds, onZoom }) => {
  const map = useMapEvents({
    click: (e) => onPick && onPick(e.latlng.lat, e.latlng.lng),
    moveend: () => emit(),
    zoomend: () => onZoom && onZoom(map.getZoom()),
  });
  const emit = () => {
    const b = map.getBounds();
    onBounds && onBounds({ latMin: b.getSouth(), latMax: b.getNorth(), lonMin: b.getWest(), lonMax: b.getEast() });
  };
  useEffect(() => {
    emit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

export const MapView = ({ location, theme, liveData, layer, radar, windGrid, windTs, scalarGrid, fires, firesVisible, onPick, onBounds, picking, mapRef }) => {
  const center = useMemo(() => [location.lat, location.lon], [location.lat, location.lon]);
  const [zoom, setZoom] = useState(7);
  const { host, current, next } = radar;

  return (
    <div className="fixed inset-0 z-0 windy-map" data-testid="radar-map-container">
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={7}
        minZoom={3}
        maxZoom={18}
        zoomControl={false}
        attributionControl={false}
        className="w-full h-full"
        style={{ background: theme === "dark" ? "#0b1220" : "#e6eef5" }}
      >
        <TileLayer key={theme} url={BASE[theme] || BASE.dark} maxNativeZoom={16} maxZoom={18} />

        {layer === "radar" && current && (
          <>
            <TileLayer key={current.path} url={radarUrl(host, current)} opacity={0.75} tileSize={256} />
            {next && next.path !== current.path && (
              <TileLayer key={`pre-${next.path}`} url={radarUrl(host, next)} opacity={0} tileSize={256} />
            )}
          </>
        )}
        {["clouds", "precip"].includes(layer) && liveData && (
          <TileLayer key={layer} url={owmTileUrl(layer)} opacity={0.85} />
        )}
        <TileLayer key={`lbl-${theme}`} url={LABELS[theme] || LABELS.dark} maxNativeZoom={16} maxZoom={18} zIndex={10} />
        {zoom >= DETAIL_ZOOM && (
          <TileLayer
            key={`osm-${theme}`}
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            zIndex={20}
            opacity={theme === "dark" ? 0.9 : 0.95}
            className={`osm-detail ${theme === "dark" ? "osm-dark" : ""}`}
          />
        )}

        <WindLayer grid={windGrid} ts={windTs} theme={theme} enabled={layer === "wind"} />
        <ScalarLayer grid={scalarGrid} variable={layer} ts={windTs} theme={theme} enabled={SCALAR_LAYERS.includes(layer)} />

        <FireLayer fires={fires} enabled={layer === "wind" && firesVisible} />
        <Marker position={center} icon={pin} />
        {picking && <Marker position={[picking.lat, picking.lon]} icon={pickPin} />}
        <Recenter lat={location.lat} lon={location.lon} />
        <Events onPick={onPick} onBounds={onBounds} onZoom={setZoom} />
      </MapContainer>
    </div>
  );
};
